import { LanguageEnum, mapCountries } from "lopermut"
import { z } from "zod"
import { CapabilityExecutionStatus } from "../capabilities/dtos"
import {
  ConversationFailureTypeEnum,
  ConversationFlowEnum,
  ConversationStatusEnum,
} from "../conversations/response_data"
import { GenderEnum } from "../customers/response_data"
import { SentimentEnum } from "../shared/common"
import { WorkflowConditionLLMPromptConfigSchema, CampaignSingleConditionRuleSchema } from "./dtos"
import { WorkflowConditionFieldEnum, WorkflowConditionOperatorEnum } from "./response_data"

export const WorkflowConditionKind = {
  Integer: "Integer",
  Enum: "Enum",
  Exists: "Exists",
  Prompt: "Prompt",
} as const

export const WorkflowConditionOperatorKindExists = [
  WorkflowConditionOperatorEnum.IsNull,
  WorkflowConditionOperatorEnum.IsNotNull,
]

const WorkflowConditionOperatorKindInteger = [
  WorkflowConditionOperatorEnum.Equals,
  WorkflowConditionOperatorEnum.NotEquals,
  WorkflowConditionOperatorEnum.GreaterThan,
  WorkflowConditionOperatorEnum.GreaterThanOrEqual,
  WorkflowConditionOperatorEnum.LessThan,
  WorkflowConditionOperatorEnum.LessThanOrEqual,
]

const WorkflowConditionOperatorKindEnum = [
  WorkflowConditionOperatorEnum.Equals,
  WorkflowConditionOperatorEnum.NotEquals,
]

const WorkflowConditionOperatorKind = {
  [WorkflowConditionKind.Integer]: WorkflowConditionOperatorKindInteger,
  [WorkflowConditionKind.Enum]: WorkflowConditionOperatorKindEnum,
  [WorkflowConditionKind.Exists]: WorkflowConditionOperatorKindExists,
}

type WorkflowConditionDefinitionType =
  | {
      kind: typeof WorkflowConditionKind.Enum
      operators: Array<keyof typeof WorkflowConditionOperatorEnum>
      values: Record<string, string>
      isTranslatable: boolean
    }
  | {
      kind: typeof WorkflowConditionKind.Integer
      operators: Array<keyof typeof WorkflowConditionOperatorEnum>
      min?: number
      max?: number
    }
  | {
      kind: typeof WorkflowConditionKind.Prompt
      operators: Array<keyof typeof WorkflowConditionOperatorEnum>
    }

export const WorkflowConditionDefinition = {
  [WorkflowConditionFieldEnum.NPSValue]: {
    kind: WorkflowConditionKind.Integer,
    operators: [...WorkflowConditionOperatorKind.Integer, ...WorkflowConditionOperatorKind.Exists],
    min: 0,
    max: 10,
  },
  [WorkflowConditionFieldEnum.OverallSentiment]: {
    kind: WorkflowConditionKind.Enum,
    operators: [...WorkflowConditionOperatorKind.Enum, ...WorkflowConditionOperatorKind.Exists],
    values: SentimentEnum,
    isTranslatable: true,
  },
  [WorkflowConditionFieldEnum.ConversationStatus]: {
    kind: WorkflowConditionKind.Enum,
    operators: WorkflowConditionOperatorKind.Enum,
    values: ConversationStatusEnum,
    isTranslatable: true,
  },
  [WorkflowConditionFieldEnum.ConversationFailureType]: {
    kind: WorkflowConditionKind.Enum,
    operators: WorkflowConditionOperatorKind.Enum,
    values: ConversationFailureTypeEnum,
    isTranslatable: true,
  },
  [WorkflowConditionFieldEnum.ConversationFlow]: {
    kind: WorkflowConditionKind.Enum,
    operators: WorkflowConditionOperatorKind.Enum,
    values: ConversationFlowEnum,
    isTranslatable: true,
  },
  [WorkflowConditionFieldEnum.CustomerAge]: {
    kind: WorkflowConditionKind.Integer,
    operators: [...WorkflowConditionOperatorKind.Integer, ...WorkflowConditionOperatorKind.Exists],
  },
  [WorkflowConditionFieldEnum.CustomerGender]: {
    kind: WorkflowConditionKind.Enum,
    operators: [...WorkflowConditionOperatorKind.Enum, ...WorkflowConditionOperatorKind.Exists],
    values: GenderEnum,
    isTranslatable: true,
  },
  [WorkflowConditionFieldEnum.CustomerCountry]: {
    kind: WorkflowConditionKind.Enum,
    operators: [...WorkflowConditionOperatorKind.Enum, ...WorkflowConditionOperatorKind.Exists],
    values: mapCountries(),
    isTranslatable: false,
  },
  [WorkflowConditionFieldEnum.CustomerLocale]: {
    kind: WorkflowConditionKind.Enum,
    operators: [...WorkflowConditionOperatorKind.Enum, ...WorkflowConditionOperatorKind.Exists],
    values: LanguageEnum,
    isTranslatable: false,
  },
  [WorkflowConditionFieldEnum.CapabilityExecutionStatus]: {
    kind: WorkflowConditionKind.Enum,
    operators: WorkflowConditionOperatorKind.Enum,
    values: CapabilityExecutionStatus,
    isTranslatable: true,
  },
  [WorkflowConditionFieldEnum.LLMPrompt]: {
    kind: WorkflowConditionKind.Prompt,
    operators: [
      WorkflowConditionOperatorEnum.ReturnsTrue,
      WorkflowConditionOperatorEnum.ReturnsFalse,
    ],
  },
} satisfies Record<keyof typeof WorkflowConditionFieldEnum, WorkflowConditionDefinitionType>

export function validateWorkflowCondition(
  condition: z.infer<typeof CampaignSingleConditionRuleSchema>,
) {
  const field = WorkflowConditionDefinition[condition.field]

  switch (field.kind) {
    case WorkflowConditionKind.Enum:
      const operatorIsAllowed = field.operators.some((operator) => operator === condition.operator)
      const needsValue = conditionValueKind(condition.field, condition.operator) !== "none"
      const valueIsAllowed = needsValue
        ? typeof condition.value === "string" && condition.value in field.values
        : true

      return operatorIsAllowed && valueIsAllowed

    case WorkflowConditionKind.Integer:
      if (conditionValueKind(condition.field, condition.operator) === "none") {
        return field.operators.some((operator) => operator === condition.operator)
      }

      const numericValue = Number(condition.value)

      const isValidNumber = Number.isInteger(numericValue)
      const isInMinRange = "min" in field ? numericValue >= field.min : true
      const isInMaxRange = "max" in field ? numericValue <= field.max : true

      return (
        field.operators.some((operator) => operator === condition.operator) &&
        isValidNumber &&
        isInMinRange &&
        isInMaxRange
      )

    case WorkflowConditionKind.Prompt:
      return (
        field.operators.some((operator) => operator === condition.operator) &&
        (condition.value === undefined || condition.value === null) &&
        WorkflowConditionLLMPromptConfigSchema.safeParse(condition.config).success
      )
  }
}

export function conditionValueKind(
  field?: keyof typeof WorkflowConditionDefinition,
  operator?: WorkflowConditionDefinitionType["operators"][number],
): "none" | "input" | "select" {
  if (!field) return "none"

  if (
    WorkflowConditionOperatorKindExists.includes(
      operator as (typeof WorkflowConditionOperatorKindExists)[number],
    )
  ) {
    return "none"
  }

  const kind = WorkflowConditionDefinition[field].kind

  if (kind === WorkflowConditionKind.Integer) {
    return "input"
  }

  if (kind === WorkflowConditionKind.Enum) {
    return "select"
  }

  return "none"
}
